import { Monitor, Moon, Sun } from 'lucide-react';
import { msg } from '@/shared/i18n/msg';
import { useTranslation } from '@/shared/i18n/use-translation';
import type { ThemeChoice } from '@/shared/lib/theme/theme-choice';
import { themeStore } from '@/shared/lib/theme/theme-store';
import { useTheme } from '@/shared/lib/theme/use-theme';
import { Button } from '@/shared/ui/button/button';
import { DropdownMenu } from '@/shared/ui/dropdown-menu/dropdown-menu';
import { DropdownMenuContent } from '@/shared/ui/dropdown-menu/dropdown-menu-content';
import { DropdownMenuGroup } from '@/shared/ui/dropdown-menu/dropdown-menu-group';
import { DropdownMenuLabel } from '@/shared/ui/dropdown-menu/dropdown-menu-label';
import { DropdownMenuRadioGroup } from '@/shared/ui/dropdown-menu/dropdown-menu-radio-group';
import { DropdownMenuRadioItem } from '@/shared/ui/dropdown-menu/dropdown-menu-radio-item';
import { DropdownMenuTrigger } from '@/shared/ui/dropdown-menu/dropdown-menu-trigger';

const THEMES = [
  { value: 'light', label: msg('Light'), icon: Sun },
  { value: 'dark', label: msg('Dark'), icon: Moon },
  { value: 'system', label: msg('Same as the system'), icon: Monitor },
] as const;

/** Light, dark or the system's: an icon button showing the current choice. */
export function ThemeMenu() {
  const { t } = useTranslation();
  const { choice } = useTheme();
  const Icon = THEMES.find((theme) => theme.value === choice)?.icon ?? Monitor;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={<Button variant="ghost" size="icon" aria-label={t('Theme')} title={t('Theme')} />}
      >
        <Icon />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        <DropdownMenuGroup>
          <DropdownMenuLabel>{t('Theme')}</DropdownMenuLabel>
          {/* The values are ThemeChoice literals. */}
          <DropdownMenuRadioGroup
            value={choice}
            onValueChange={(value) => themeStore.set(value as ThemeChoice)}
          >
            {THEMES.map(({ value, label, icon: ItemIcon }) => (
              <DropdownMenuRadioItem key={value} value={value}>
                <ItemIcon /> {t(label)}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
